import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useUserLocation } from '../../contexts/UserLocationContext';
import { openAppSettings } from '../../utils/openAppSettings';

const statusLabels = {
  idle: 'لم يُطلب بعد',
  loading: 'جاري الطلب...',
  granted: 'مسموح',
  denied: 'مرفوض',
  timeout: 'انتهت المهلة',
  error: 'خطأ',
  insecure: 'السياق غير آمن',
};

/**
 * بطاقة إعدادات الموقع فقط (بدون غلاف الصفحة).
 * تُستخدم داخل صفحة الإعدادات أو صفحة إعدادات الموقع المستقلة.
 */
export default function LocationSettingsCard() {
  const { userLocation, locationStatus, requestLocation } = useUserLocation();
  const [openingSettings, setOpeningSettings] = useState(false);
  const isRequesting = locationStatus === 'loading';
  const isDenied = ['denied', 'error', 'timeout'].includes(locationStatus);
  const isNative = Capacitor.isNativePlatform();

  const handleOpenAppSettings = async () => {
    setOpeningSettings(true);
    try {
      await openAppSettings();
    } finally {
      setOpeningSettings(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="MapPin" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">إعدادات الموقع</h2>
          <p className="text-sm text-slate-500">تفعيل الموقع لعرض المصورين القريبين وتحديد موقعك على الخريطة. لا تحتاج لتسجيل الدخول.</p>
        </div>
      </div>

      {isDenied && (
        <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-sm text-amber-800 mb-3">
            تم رفض إذن الموقع سابقاً. لتفعيله، افتح إعدادات التطبيق واختر «الموقع» ثم اسمح للتطبيق بالوصول.
          </p>
          {isNative ? (
            <Button
              variant="default"
              size="sm"
              onClick={handleOpenAppSettings}
              loading={openingSettings}
              iconName="Settings"
              iconPosition="right"
              className="bg-amber-600 hover:bg-amber-700"
            >
              فتح إعدادات التطبيق
            </Button>
          ) : (
            <p className="text-xs text-amber-700">
              في المتصفح: افتح إعدادات الموقع من أيقونة القفل في شريط العنوان واسمح بالموقع لهذه الصفحة.
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-slate-100">
        <div className="text-sm">
          <span className="text-slate-600">الحالة: </span>
          <span className={
            locationStatus === 'granted' ? 'text-green-600 font-medium' :
            isDenied ? 'text-amber-600 font-medium' :
            'text-slate-700'
          }>
            {statusLabels[locationStatus] || locationStatus}
          </span>
          {userLocation?.lat != null && (
            <span className="block text-slate-500 mt-1 text-xs">
              الإحداثيات: {userLocation.lat.toFixed(5)}, {userLocation.lng.toFixed(5)}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => requestLocation()}
          disabled={isRequesting || typeof navigator === 'undefined' || !navigator?.geolocation}
          loading={isRequesting}
          iconName="RefreshCw"
          iconPosition="right"
        >
          {locationStatus === 'granted' ? 'تحديث الموقع' : 'إعادة المحاولة'}
        </Button>
      </div>

      <p className="text-slate-500 text-xs mt-4">
        يمكنك أيضاً طلب الإذن عند فتح الخريطة أو الضغط على «موقعي الحالي» أو في قسم المصورين القريبين.
      </p>
    </div>
  );
}
